import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpEvent } from '@angular/common/http';
import { Observable, map, filter, retry, timer, catchError, throwError } from 'rxjs';
import {
  MediaPresignedURLPayload,
  MediaPresignedURLResponse,
  MediaUploadProgress
} from '../projects/media-scan/media-scan.types';
import { Endpoints } from '../endpoints';

@Injectable({
  providedIn: 'root'
})
export class MediaUploadService {
  private readonly maxRetries = 3;
  private readonly retryDelay = 1000;

  constructor(private readonly http: HttpClient) {}

  getPresignedUrl(payload: MediaPresignedURLPayload, projectId: string): Observable<MediaPresignedURLResponse> {
    return this.http.post<MediaPresignedURLResponse>(
      Endpoints.getMediaUploadPresignedUrl(projectId),
      payload
    ).pipe(
      retry({
        count: this.maxRetries,
        delay: (error, retryCount) => {
          const delay = this.retryDelay * Math.pow(2, retryCount - 1);
          return timer(delay);
        }
      }),
      catchError(error => {
        console.error('Failed to get presigned URL:', error);
        return throwError(() => new Error('Failed to get upload URL'));
      })
    );
  }

  uploadMediaFile(url: string, file: File): Observable<MediaUploadProgress> {
    return this.http.put(url, file, {
      headers: { 'Content-Type': file.type },
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map((event: HttpEvent<unknown>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = event.total
              ? Math.round((event.loaded / event.total) * 100)
              : 0;
            return { progress, completed: false };

          case HttpEventType.Response:
            return { progress: 100, completed: true };

          default:
            return { progress: 0, completed: false };
        }
      }),
      filter(result => result.progress > 0 || result.completed),
      retry({
        count: this.maxRetries,
        delay: (error, retryCount) => {
          const delay = this.retryDelay * Math.pow(2, retryCount - 1);
          return timer(delay);
        }
      }),
      catchError(error => {
        console.error('Upload failed:', error);
        return throwError(() => new Error('Upload failed'));
      })
    );
  }

  normalizeFilename(projectId: string, filename: string): string {
    const fileParts = filename.split('.');
    const extension = fileParts.length > 1 ? fileParts.pop() : '';
    const baseName = fileParts.join('.');
    const normalizedBaseName = baseName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    return `${projectId}/${normalizedBaseName}.${extension}`;
  }
}
